import React, { Fragment, useEffect, useLayoutEffect, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { BOTTOM_INDENTS, COLORS } from '@constants';
import { useStore } from '@hooks';
import { Button, Icon, Loader, Select, Tooltip } from '@ui';
import { formatString } from '@utils';
import { Col, Row } from 'antd';
import { CategoryScale, Chart as ChartJS, Legend, LinearScale, LineElement, PointElement, Title } from 'chart.js';
import { observer } from 'mobx-react';
import { getSnapshot } from 'mobx-state-tree';
import {
    ALL_FILTER_VALUE,
    CUSTOMER_EXCLUDED_METRICS,
    CUSTOMER_OPTIONS,
    DETAILIZATION_TAB_KEYS,
    DETAILIZATION_TABS,
    DEVICES_OPTIONS,
    FILTER_PARAMS,
    MENU_ITEM_IDS,
    PERFORMANCE_METRICS
} from '../../constants';
import { usePerformanceScrollFetch } from '../../hooks';
import CheckboxCard from './components/CheckboxCard';
import CheckboxCardsPlaceholder from './components/CheckboxCardsPlaceholder';
import { useDidMountEffect } from './hooks/useDidMountEffect';
import { CHART_OPTIONS } from './constants';
import {
    ChartLabel,
    ChartLabelsContainer,
    QuestionCircleIcon,
    StyledChart,
    StyledTabs,
    TooltipWrapper
} from './styled';
import { getMonthSubLabelsPluginConfig } from './utils';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const DOLLAR_METRICS = ['cpa', 'revenue', 'aov', 'spend'];

const Overview = observer(({ date, filters, onFiltersChange, onComponentViewUpdate }) => {
    const { campaign, device, customer, detailizationTab } = filters;
    const {
        campaign: onCampaignChange,
        device: onDeviceChange,
        customer: onCustomerChange,
        detailizationTab: onDetailizationTabChange
    } = onFiltersChange;

    const store = useStore();
    const {
        getOverview,
        detailizationTab: storeDetalizationTab,
        performanceList,
        pendingMetricsChart,
        overviewPending,
        pendingMetricsOverview,
        getChartData,
        getMetricsOverview,
        getCampaignsForPerformance,
        chartDatasets,
        selectedChartMetrics,
        chartLabels,
        setDetailizationTab,
        downloadChartData,
        performanceCampaigns
    } = store.performanceStore;
    const [initialFetchDone, setInitialFetch] = useState(false);

    const isDeviceFiltered = device !== ALL_FILTER_VALUE && device !== undefined;
    const isCustomerFiltered = customer !== ALL_FILTER_VALUE && customer !== undefined;
    const isFilterApplied = isDeviceFiltered || isCustomerFiltered;

    useLayoutEffect(() => {
        if (storeDetalizationTab !== detailizationTab) setDetailizationTab(detailizationTab);
    }, []);
    const [chartIndicator, setChartIndicator] = useState('pcs');

    const defaultProps = {
        ...date,
        ...(campaign && campaign !== ALL_FILTER_VALUE ? { [FILTER_PARAMS.CAMPAIGN_ID]: campaign } : {}),
        ...(device && device !== ALL_FILTER_VALUE ? { [FILTER_PARAMS.DEVICE_TYPE]: device } : {}),
        ...(customer && customer !== ALL_FILTER_VALUE ? { [FILTER_PARAMS.CUSTOMER_TYPE]: customer } : {})
    };

    const { componentRef } = usePerformanceScrollFetch({
        initialFetchDone,
        onInitialFetch: () => {
            setInitialFetch(true);
            getOverview({
                metrics: PERFORMANCE_METRICS,
                ...defaultProps
            });
        },
        onReFetch: () => null,
        onComponentViewUpdate,
        date
    });

    useEffect(() => {
        getCampaignsForPerformance();
    }, []);

    useEffect(() => {
        const selected = getSnapshot(selectedChartMetrics);

        const includesDollarMetrics = selected.some((item) => DOLLAR_METRICS.includes(item));
        const includesOtherMetrics = selected.some((item) => !DOLLAR_METRICS.includes(item));

        if (includesDollarMetrics && includesOtherMetrics) {
            const firstIsDollar = DOLLAR_METRICS.includes(selected[0]);
            setChartIndicator(firstIsDollar ? 'pcs and $' : '$ and pcs');
        } else if (includesDollarMetrics) {
            setChartIndicator('$');
        } else {
            setChartIndicator('pcs');
        }
    }, [selectedChartMetrics.length]);

    useDidMountEffect(() => {
        getChartData({
            ...defaultProps
        });
    }, [detailizationTab]);

    useDidMountEffect(() => {
        getChartData(defaultProps);
        getMetricsOverview({
            metrics: PERFORMANCE_METRICS,
            ...defaultProps
        });
    }, [date, campaign, device, customer]);

    const chart = {
        data: { datasets: chartDatasets, labels: chartLabels },
        options: {
            ...CHART_OPTIONS,
            layout: {
                ...CHART_OPTIONS.layout,
                padding: {
                    ...CHART_OPTIONS.layout.padding,
                    bottom: detailizationTab !== DETAILIZATION_TAB_KEYS.MONTHS ? 24 : 0
                }
            },
            scales: {
                ...CHART_OPTIONS.scales,
                x: {
                    ...CHART_OPTIONS.scales.x,
                    offset: chartLabels.length < 2
                }
            }
        }
    };

    const campaigns = [
        {
            label: <span data-e2e='performance_all-campaigns_dropdown-item'>All campaigns</span>,
            value: 'all'
        },
        ...(performanceCampaigns
            ?.filter((item) => item.id != null)
            .map((item) => {
                return {
                    label: <span data-e2e={`performance_${formatString(item.title)}_dropdown-item`}>{item.title}</span>,
                    value: String(item.id)
                };
            }) ?? [])
    ];

    return (
        <div
            ref={componentRef}
            id={MENU_ITEM_IDS.OVERVIEW}
            style={{ scrollMarginTop: '180px' }}
            data-e2e='performance_overview-section'
        >
            <Row style={{ ...BOTTOM_INDENTS.XL_PLUS }} gutter={[16, 16]}>
                <Col flex='1 0 auto'>
                    <Select
                        data-e2e='performance_campaigns_dropdown'
                        label='Campaign'
                        defaultValue={campaigns[0]?.value}
                        options={campaigns}
                        value={campaign}
                        onChange={onCampaignChange}
                    />
                </Col>
                <Col flex='1 0 auto'>
                    <Select
                        data-e2e='performance_device_dropdown'
                        label='Device'
                        defaultValue={DEVICES_OPTIONS[0].value}
                        options={DEVICES_OPTIONS}
                        value={device}
                        onChange={onDeviceChange}
                    />
                </Col>
                <Col flex='1 0 auto'>
                    <Select
                        data-e2e='performance_customer_dropdown'
                        label='Customer'
                        defaultValue={CUSTOMER_OPTIONS[0].value}
                        options={CUSTOMER_OPTIONS}
                        value={customer}
                        onChange={onCustomerChange}
                    />
                </Col>
                <Col flex='0 0 auto'>
                    <Button
                        data-e2e='performance_download-scv_top-btn'
                        onClick={() => downloadChartData(defaultProps)}
                        icon={<Icon size='24px' color={COLORS.$gray60Black} component={() => <Icon.DownloadIcon />} />}
                        disabled={pendingMetricsChart || overviewPending}
                    >
                        Download CSV
                    </Button>
                </Col>
            </Row>
            <Fragment>
                <StyledChart data-e2e='performance_chart'>
                    {pendingMetricsChart || overviewPending ? <Loader overlayColor='white' borderRadius='8px' /> : null}
                    <ChartLabelsContainer>
                        {chartIndicator === '$' && <ChartLabel>$</ChartLabel>}
                        {chartIndicator === 'pcs' && <ChartLabel>pcs</ChartLabel>}
                        {chartIndicator === '$ and pcs' && (
                            <React.Fragment>
                                <ChartLabel>$</ChartLabel>
                                <ChartLabel>pcs</ChartLabel>
                            </React.Fragment>
                        )}
                        {chartIndicator === 'pcs and $' && (
                            <React.Fragment>
                                <ChartLabel>pcs</ChartLabel>
                                <ChartLabel>$</ChartLabel>
                            </React.Fragment>
                        )}
                    </ChartLabelsContainer>
                    {isFilterApplied ? (
                        <TooltipWrapper>
                            <Tooltip
                                title="Boost costs are not included in spend on this chart, as they are allocated to a campaign's overall Priority Score (versus individual orders)"
                                trigger='hover'
                                placement='top'
                            >
                                <Icon
                                    component={() => <QuestionCircleIcon />}
                                    size='24px'
                                    color={COLORS.$gray40Black}
                                    stroke='white'
                                />
                            </Tooltip>
                        </TooltipWrapper>
                    ) : null}
                    <Line
                        data={chart.data}
                        options={chart.options}
                        plugins={[getMonthSubLabelsPluginConfig(detailizationTab, chartDatasets)]}
                        redraw
                    />
                </StyledChart>
                <StyledTabs
                    data-e2e='performance_chart-detalization_section'
                    onChange={(val) => {
                        setDetailizationTab(val);
                        onDetailizationTabChange(val);
                    }}
                    noBottomLine
                    centered
                    defaultActiveKey={detailizationTab}
                    items={DETAILIZATION_TABS}
                />
                <Row gutter={[16, 16]}>
                    {pendingMetricsOverview || overviewPending ? (
                        <CheckboxCardsPlaceholder data-e2e='performance_card_placeholder' />
                    ) : (
                        performanceList
                            ?.filter((item) =>
                                customer === 'new' || customer === 'returning'
                                    ? !CUSTOMER_EXCLUDED_METRICS.includes(item.label)
                                    : true
                            )
                            ?.map((metric) => {
                                return (
                                    <CheckboxCard
                                        key={metric.id}
                                        date={date}
                                        metric={metric}
                                        isFilterApplied={isFilterApplied}
                                    />
                                );
                            })
                    )}
                </Row>
            </Fragment>
        </div>
    );
});

export default Overview;
